import { useCallback, useEffect, useState, type ReactElement } from "react";

import { api, ApiError, apiList } from "../../api/client";
import type { DeidentifiedParticipant, SubstudyInfo, SubstudyParticipant } from "../../api/types";
import { SkeletonRows } from "../Skeleton";
import { TabPanel } from "../Tabs";

// The substudy roster, read-only. Rows appear when subjects register with a
// pre-loaded study ID (/register/study-id) or arrive in an admin batch upload.
// The "add a participant" invite form was removed with the join-code system
// (2026-08-14) — there is no email path into a substudy anymore. NeuroAge
// gets the deidentified roster instead: study codes, photo counts and median
// FaceAge for the baseline and retest windows — no PHI.
export function ParticipantsTab(): ReactElement {
  const [participants, setParticipants] = useState<SubstudyParticipant[]>([]);
  const [deidRows, setDeidRows] = useState<DeidentifiedParticipant[]>([]);
  const [substudy, setSubstudy] = useState<SubstudyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      // The substudy decides which roster endpoint applies — fetch it first so
      // a deidentified console never touches the PHI endpoint (which 403s).
      const info = await api<SubstudyInfo>("GET", "/collab/substudy");
      setSubstudy(info);
      if (info.deidentified) {
        setDeidRows(await apiList<DeidentifiedParticipant>("/collab/participants/deidentified"));
      } else {
        setParticipants(await apiList<SubstudyParticipant>("/collab/participants"));
      }
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load participants.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <>
      <TabPanel id="participants">
        {substudy?.has_access_codes ? (
          <p className="muted">
            Participants join with personal study IDs. Subjects sign up at{" "}
            <code>
              {window.location.origin}/join/{substudy.slug}
            </code>{" "}
            with the ID your team gave them; there is no shared study code or email sign-up.
          </p>
        ) : null}
      </TabPanel>

      <section className="app-card">
        <h2>Substudy Participants</h2>
        {loading ? (
          <SkeletonRows rows={3} />
        ) : error ? (
          <p className="form-error">{error}</p>
        ) : substudy?.deidentified ? (
          deidRows.length === 0 ? (
            <p className="muted">No participants yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>NeuroAge ID</th>
                  <th>Baseline photos</th>
                  <th>Median baseline FaceAge</th>
                  <th>Retest photos</th>
                  <th>Median retest FaceAge</th>
                </tr>
              </thead>
              <tbody>
                {deidRows.map((r) => (
                  <tr key={r.study_code}>
                    <td>{r.study_code}</td>
                    <td>{r.baseline_photos}</td>
                    <td>{r.median_baseline_faceage?.toFixed(2) ?? "—"}</td>
                    <td>{r.eoc_photos}</td>
                    <td>{r.median_eoc_faceage?.toFixed(2) ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : participants.length === 0 ? (
          <p className="muted">No participants yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Name</th>
                <th>Status</th>
                <th>Added</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{p.email ?? "—"}</td>
                  <td>
                    {[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}
                    {p.invite_identity_mismatch ? (
                      <span
                        className="muted"
                        tabIndex={0}
                        data-tooltip="The participant consented with a name or date of birth different from the invite; their entered details are the study record."
                        aria-label="The participant consented with a name or date of birth different from the invite; their entered details are the study record."
                      >
                        {" "}
                        <i className="fas fa-triangle-exclamation" aria-hidden="true" /> differs
                        from invite
                      </span>
                    ) : null}
                  </td>
                  <td>{p.enrollment_status}</td>
                  <td>{new Date(p.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
